import { createFileRoute } from '@tanstack/react-router'
import React from 'react'

import { JellyIcon } from '@/components/icons'
import {
  BrandKitCard,
  NetworkBadge,
  PersonRow,
  ProfileRow,
  SectionLabel,
  StatusBadge,
} from '@/components/shared'
import { AppLink as Link } from '@/components/shell'
import {
  AssetStrip,
  ChatRow,
  ClientHeader,
  ProductionPanel,
  ShootHistoryRow,
  ShootPanel,
  VoiceCard,
} from '@/components/spravce/klienti'
import {
  ACCOUNT_FACTS,
  ASSETS,
  BRAND_KIT,
  CHAT,
  CLIENT,
  LAST_DELIVERY,
  MENU_FILES,
  NETWORK_ACCOUNTS,
  NEXT_SHOOT,
  PEOPLE,
  PRODUCTION,
  SHOOT_HISTORY,
  VOICE,
  WEEKLY_MENU,
} from '@/mocks/spravce/klientDetail'

export const Route = createFileRoute('/spravce/klienti/$client')({
  head: () => ({ meta: [{ title: 'Klient · Aplikace Bitez' }] }),
  component: SpravceKlientDetail,
})

/** One group: its label, then its content — space alone separates them. */
function Group({
  label,
  delay = 0,
  children,
}: {
  label: string
  delay?: number
  children: React.ReactNode
}) {
  return (
    <section className="animate-rise-up mt-8" style={{ animationDelay: `${delay}ms` }}>
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-3">{children}</div>
    </section>
  )
}

function SpravceKlientDetail() {
  return (
    <div className="px-4 pt-4 pb-6">
      <Link
        href="/spravce/klienti"
        className="-ml-1 flex w-fit items-center gap-1.5 rounded-full px-1 py-1 text-[10px] font-bold tracking-widest text-ink/40 uppercase transition-colors duration-150 hover:text-ink/70"
      >
        <JellyIcon name="arrow-left" className="size-3" />
        Klienti
      </Link>

      <div className="mt-3">
        <ClientHeader client={CLIENT} />
      </div>

      <div className="animate-rise-up mt-5">
        <ChatRow chat={CHAT} />
      </div>

      <Group label="Výroba">
        <ProductionPanel production={PRODUCTION} />
      </Group>

      <Group label="Focení a fotky" delay={60}>
        <ShootPanel shoot={NEXT_SHOOT} delivery={LAST_DELIVERY} />
        <div className="mt-4">
          <AssetStrip photos={ASSETS} rest="+58" note="63 fotek ze 3 focení · od listopadu" />
        </div>

        {/* The history behind the strip. Folded, because it is a lookup and not
            a thing she reads every time — and a plain <details> needs no state
            of its own, which is why the app uses it for exactly this. */}
        <details className="group mt-4">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[10px] font-bold text-ink/45 transition-colors duration-150 hover:text-ink/70">
            Předchozí focení
            <JellyIcon
              name="angle-up"
              className="size-3 rotate-180 transition-transform duration-200 group-open:rotate-0"
            />
          </summary>
          <div className="mt-3.5 space-y-3.5">
            {SHOOT_HISTORY.map((shoot) => (
              <ShootHistoryRow key={shoot.date} shoot={shoot} />
            ))}
          </div>
        </details>
      </Group>

      {/* What she writes IN — onboarding step 2 read from the other side, and
          for a content specialist the most useful block here. */}
      <Group label="Jak o nich psát" delay={120}>
        <VoiceCard voice={VOICE} />
      </Group>

      <Group label="Podklady podniku" delay={150}>
        <BrandKitCard kit={BRAND_KIT} />

        <div className="mt-3 divide-y divide-ink/5 rounded-3xl bg-white px-5 shadow-sm">
          {MENU_FILES.map((file) => (
            <button
              key={file.name}
              type="button"
              className="flex w-full cursor-pointer items-center gap-3 py-4 text-left"
            >
              <JellyIcon name="file" className="size-4.5 shrink-0 text-ink/50" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold">{file.name}</span>
                <span className="block text-[10px] font-semibold text-ink/45">{file.updated}</span>
              </span>
              <JellyIcon name="arrow-right" className="size-3.5 shrink-0 text-ink/40" />
            </button>
          ))}

          {/* The weekly menu is the client's own upload (spec §11), so what she
              needs from it is not the dishes but whether it arrived. */}
          <div className="flex w-full items-center gap-3 py-4">
            <JellyIcon name="utensils" className="size-4.5 shrink-0 text-ink/50" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-bold">{WEEKLY_MENU.label}</span>
              <span className="block text-[10px] font-semibold text-ink/45">
                {WEEKLY_MENU.state}
              </span>
            </span>
            <StatusBadge label="Běží" icon="check" variant="soft" />
          </div>
        </div>
      </Group>

      <Group label="Lidé" delay={200}>
        <div className="space-y-3.5">
          {PEOPLE.map((person) => (
            <PersonRow key={person.name} person={person} />
          ))}
        </div>
        {/* Spec §9 fotograf → klient: how the venue is to shoot at. Internal,
            quiet, and useful before she promises the client anything. */}
        <p className="mt-3 flex items-center gap-1.5 text-[10px] font-semibold text-ink/45">
          <JellyIcon name="star" variant="fill" className="size-3 text-sun-dark" />
          Tomáš hodnotí podnik 4,8 — připravení, ale focení začíná později, než se domluví.
        </p>
      </Group>

      <Group label="Účet a napojení" delay={250}>
        {/* Status only, never an action (B10): only the client can re-authorise
            and the prompt reaches them automatically — do not add a button. */}
        <div className="divide-y divide-ink/5 rounded-3xl bg-white px-5 shadow-sm">
          {NETWORK_ACCOUNTS.map((network) => (
            <div key={network.name} className="flex items-center gap-3 py-4">
              <span className="shrink-0">
                <NetworkBadge network={network} size="size-5" ring="ring-white" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold">{network.name}</span>
                <span className="block truncate text-[10px] font-semibold text-ink/45">
                  {network.handle}
                </span>
              </span>
            </div>
          ))}
        </div>
        <div className="mt-3 space-y-2.5 rounded-3xl bg-white p-5 shadow-sm">
          {ACCOUNT_FACTS.map((fact) => (
            <p key={fact.label} className="flex items-baseline gap-1.5 text-sm">
              <span className="text-ink/70">{fact.label}</span>
              <span aria-hidden className="min-w-4 flex-1 border-b-2 border-dotted border-ink/15" />
              <span className="font-bold whitespace-nowrap">{fact.value}</span>
            </p>
          ))}
        </div>

        <div className="mt-3">
          <ProfileRow
            icon="money-bill"
            title="Fakturace a smlouva"
            note="Zakládá a mění manažer — vy jen čtete"
          />
        </div>
      </Group>
    </div>
  )
}
